import { useState, type KeyboardEvent } from 'react';
import type { Project } from '../types';

interface LeadingOption {
  value: string;
  label: string;
}

interface ProjectSelectProps {
  id?: string;
  ariaLabel?: string;
  projects: Project[];
  value: string;
  onChange: (projectId: string) => void;
  onCreateProject: (name: string) => Promise<Project | undefined>;
  /** Non-project options rendered above the project list — "All projects" /
   * "No project" in the task filter, "None" in the task editor. */
  leadingOptions?: LeadingOption[];
  /** How to label each project option. Defaults to just the name; the task
   * editor appends the org priority it's tagged with. */
  projectLabel?: (project: Project) => string;
}

const NEW_PROJECT_VALUE = '__new_project__';

/**
 * A project <select> with project creation built in — picking
 * "+ New project…" swaps the dropdown for a small inline name field so a
 * project can be created right where it's needed (a task filter, a task's
 * editor) instead of requiring a trip to the Priorities tab. Renaming,
 * deleting, and priority-tagging still live there; this is just fast,
 * in-context creation of a new one.
 */
export function ProjectSelect({
  id,
  ariaLabel,
  projects,
  value,
  onChange,
  onCreateProject,
  leadingOptions = [],
  projectLabel = (p) => p.name,
}: ProjectSelectProps) {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit() {
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    try {
      const project = await onCreateProject(trimmed);
      // onCreateProject already reports failures via the app's toast system
      // and returns undefined — stay in the inline form so the typed name
      // isn't lost and the person can just retry.
      if (project) {
        onChange(project.id);
        setCreating(false);
        setName('');
      }
    } finally {
      setBusy(false);
    }
  }

  function cancel() {
    setCreating(false);
    setName('');
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      submit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    }
  }

  if (creating) {
    return (
      <div className="field-inline project-create">
        <input
          autoFocus
          type="text"
          placeholder="Project name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={busy}
          aria-label="New project name"
        />
        <button type="button" className="btn sm primary" onClick={submit} disabled={!name.trim() || busy}>
          {busy ? <span className="spin" /> : 'Add'}
        </button>
        <button type="button" className="btn sm ghost" onClick={cancel} disabled={busy} aria-label="Cancel">
          ×
        </button>
      </div>
    );
  }

  return (
    <select
      id={id}
      aria-label={ariaLabel}
      value={value}
      onChange={(e) => {
        if (e.target.value === NEW_PROJECT_VALUE) {
          setCreating(true);
        } else {
          onChange(e.target.value);
        }
      }}
    >
      {leadingOptions.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
      {projects.map((p) => (
        <option key={p.id} value={p.id}>
          {projectLabel(p)}
        </option>
      ))}
      {projects.length > 0 && <option disabled>──────────</option>}
      <option value={NEW_PROJECT_VALUE}>+ New project…</option>
    </select>
  );
}
